import { Autocomplete, Checkbox, Container, FormControlLabel, Grid, TextField, ThemeProvider, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Bar } from './Bar';
import { DrugGrid } from './DrugGrid';
import { Footer } from './Footer';
import { Header } from './header';
import { Data, Node, NodeType } from './models';
import { theme } from './theme';

export function Drug() {
  const [data, setData] = useState<Data>();
  const [options, setOptions] = useState<Array<Node>>([])
  const [selected, setSelected] = useState<Array<Node>>([]);
  const [filteredData, setFilteredData] = useState<Array<any> | null>(null);
  const [fdaChecked, setFDAChecked] = useState(false);

  const handleFDACheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFDAChecked(event.target.checked);
  };

  useEffect(() => {
    (async () => {
      const data = new Data();
      setData(await data.load());
    })()
  }, [])

  useEffect(() => {
    setSelected([]);
    if (!data) return;
    const optCytokines = data.getNodesByType(NodeType.CYTOKINE).map(v => new Node(NodeType.CYTOKINE + "_" + v));
    const optKinases = data.getNodesByType(NodeType.KINASE).map(v => new Node(NodeType.KINASE + "_" + v));
    setOptions(optCytokines.concat(optKinases));
    setFilteredData(null)
  }, [data])

  useEffect(() => {
    data && setFilteredData(
      fdaChecked ? data.filterForDrugs(selected).filter(v => v.fdaApproved === 'Yes') : data.filterForDrugs(selected)
    )
  }, [data, selected, fdaChecked])

  return (
    <ThemeProvider theme={theme}>
      <Container sx={{ minHeight: `calc(100vh - 301px)` }}>
        <Grid container spacing={2}>

          <Header label="Drug Discovery" home={true} />
          <Grid item xs={12} sm={8}>
            <Typography variant='body1' style={{ paddingBottom: '30px' }}>
              Enter the name of cytokine(s) or kinase(s) of interest to identify potential drugs that target a given 
              cytokine or kinase. Selecting a kinase will query the cytokines it connects. A full list of drugs that 
              target the complete list of cytokines will be generated. Inspect the top drugs in terms of how impactfully 
              they are towards all selected cytokines in the bar chart. More detailed information on all drugs with impact 
              is provided in the table. Click on the drugs in the bar chart or table to bring up a new plot of the efficacy 
              spectrum of this drug across all cytokines of interest. 
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8} alignContent="baseline" >
            <Typography variant='h2' sx={{ mb: 2 }}>Search</Typography>
            <Autocomplete
              clearOnEscape
              options={options}
              multiple={true}
              value={selected}
              groupBy={(option) => { return option.type }}
              getOptionLabel={(option) => { return option.name }}
              renderInput={(params) => <TextField {...params} label="Enter Kinase or Cytokines" />}
              onChange={(event, newValue) => {
                setSelected(newValue);
              }} />
          </Grid>

          {(filteredData && filteredData.length) ? (
            <>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox size='small' color="secondary" onChange={handleFDACheck} />}
                  label='Limit results to FDA approved drugs'
                  labelPlacement="end"
                />
                {filteredData && <Bar filteredData={filteredData} />}
                {filteredData && <DrugGrid filteredData={filteredData} />}
              </Grid>
            </>) : (
            <Grid item xs={12}>
              <Typography variant='body2' color="secondary">
                Select a cytokine or kinase from the drop down list
              </Typography>
            </Grid>
          )}
        </Grid>
      </Container >
      <Footer />
    </ThemeProvider>
  );
}

export default Drug;